import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { permissionsApi, rolesApi } from '@/api';
import { ROLE_PRESET_LABELS, ROLE_PRESET_PERMISSIONS } from '@/constants/roles';
import { PageHeader } from '@/components/layout/PageHeader';
import { PermissionMatrix } from '@/components/roles/PermissionMatrix';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActionLink, TableRowActions } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganizationModules } from '@/hooks/useOrganizationModules';
import { usePagination } from '@/hooks/usePagination';
import type { PermissionResponse, RoleRequest, RoleResponse } from '@/types/role';
import { usePermissions } from '@/hooks/usePermissions';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

const empty: RoleRequest = { name: '', code: '', permissionIds: [] };

const EDITABLE_SYSTEM_ROLE_CODES = ['ORGANIZATION_OWNER'] as const;

function isRoleEditable(role: RoleResponse) {
  return !role.systemRole || EDITABLE_SYSTEM_ROLE_CODES.includes(role.code as (typeof EDITABLE_SYSTEM_ROLE_CODES)[number]);
}

function groupPermissions(perms: PermissionResponse[]) {
  const groups = new Map<string, PermissionResponse[]>();
  for (const p of perms) {
    const module = p.module ?? 'OTHER';
    const list = groups.get(module) ?? [];
    list.push(p);
    groups.set(module, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function PermissionList({ role }: { role: RoleResponse }) {
  const perms = role.permissions ?? [];
  if (perms.length === 0) {
    return <p className="text-sm text-slate-500">No permissions assigned.</p>;
  }
  const grouped = groupPermissions(perms);
  return (
    <div className="max-h-80 space-y-4 overflow-y-auto">
      {grouped.map(([module, items]) => (
        <div key={module}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{module}</p>
          <ul className="space-y-1">
            {items.map((p) => (
              <li
                key={p.id}
                className="rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800"
              >
                <span className="font-medium text-slate-800 dark:text-slate-200">{p.name}</span>
                <span className="ml-2 text-xs text-slate-500">{p.code}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function RolesPage() {
  const { has } = usePermissions();
  const canManageRoles = has('ROLE_MANAGE');
  const { modules } = useOrganizationModules();
  const enabledModuleCodes = useMemo(
    () => modules.filter((m) => m.enabled).map((m) => m.module),
    [modules],
  );
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<RoleResponse[]>([]);
  const [permissions, setPermissions] = useState<PermissionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState<RoleResponse | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editing, setEditing] = useState<RoleResponse | null>(null);
  const [form, setForm] = useState<RoleRequest>(empty);
  const [roleActive, setRoleActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    permissionsApi.list({ size: 500 }).then((p) => setPermissions(p.content)).catch(() => {});
  }, []);

  const applyPreset = (presetKey: string) => {
    const codes = ROLE_PRESET_PERMISSIONS[presetKey];
    if (!codes) return;
    const ids = permissions.filter((p) => codes.includes(p.code)).map((p) => p.id);
    setForm((f) => ({ ...f, permissionIds: ids }));
    notify.success('Preset applied — adjust if needed');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rolesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const openView = async (role: RoleResponse) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewing(role);
    try {
      const full = await rolesApi.get(role.id);
      setViewing(full);
    } catch (err) {
      showApiError(err, 'Failed to load role permissions');
    } finally {
      setViewLoading(false);
    }
  };

  const openEdit = async (role: RoleResponse) => {
    setEditing(role);
    setModalOpen(true);
    setRoleActive(role.status !== false);
    try {
      const full = await rolesApi.get(role.id);
      setForm({
        name: full.name,
        code: full.code,
        description: full.description,
        permissionIds: full.permissionIds ?? full.permissions?.map((p) => p.id) ?? [],
      });
      setRoleActive(full.status !== false);
    } catch (err) {
      showApiError(err, 'Failed to load role');
      setForm({
        name: role.name,
        code: role.code,
        description: role.description,
        permissionIds: role.permissionIds ?? [],
      });
    }
  };

  const columns: Column<RoleResponse>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (r) =>
        r.systemRole ? (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Built-in
          </span>
        ) : (
          <span className="rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-950 dark:text-primary-300">
            Custom
          </span>
        ),
    },
    { key: 'name', header: 'Role name' },
    { key: 'code', header: 'Code' },
    {
      key: 'description',
      header: 'Description',
      render: (r) => (
        <span className="text-slate-600 dark:text-slate-400">{r.description ?? '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        r.status ? (
          <span className="text-emerald-600">Active</span>
        ) : (
          <span className="text-slate-500">Inactive</span>
        ),
    },
    {
      key: 'permissions',
      header: 'Permissions',
      render: (r) => r.permissions?.length ?? r.permissionIds?.length ?? 0,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <TableRowActions>
          <TableRowActionLink className="inline-flex items-center gap-1" onClick={() => openView(r)}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
            View
          </TableRowActionLink>
          {canManageRoles && !r.systemRole && (
            <TableRowActionLink onClick={() => openEdit(r)}>Edit</TableRowActionLink>
          )}
          {canManageRoles && r.systemRole && isRoleEditable(r) && (
            <TableRowActionLink onClick={() => openEdit(r)}>Edit permissions</TableRowActionLink>
          )}
        </TableRowActions>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, status: roleActive };
      if (editing) {
        await rolesApi.update(editing.id, payload);
      } else {
        await rolesApi.create(payload);
      }
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Roles & permissions"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Roles' }]}
        actions={
          canManageRoles ? (
            <Button
              onClick={() => {
                setEditing(null);
                setForm(empty);
                setRoleActive(true);
                setModalOpen(true);
              }}
            >
              <AddIcon fontSize="small" /> Custom role
            </Button>
          ) : undefined
        }
      />

      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />

      <Modal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={viewing ? `${viewing.name} — permissions` : 'Role permissions'}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setViewOpen(false)}>
            Close
          </Button>
        }
      >
        {viewLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : viewing ? (
          <>
            <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
              <span className="font-medium">Code:</span> {viewing.code}
              {viewing.description && (
                <span className="ml-3">
                  <span className="font-medium">Description:</span> {viewing.description}
                </span>
              )}
            </p>
            <PermissionList role={viewing} />
          </>
        ) : null}
      </Modal>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          editing
            ? editing.code === 'ORGANIZATION_OWNER'
              ? 'Edit organization owner permissions'
              : 'Edit custom role'
            : 'New custom role'
        }
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="mb-4 flex flex-wrap gap-2">
          {ROLE_PRESET_LABELS.map((p) => (
            <Button key={p.key} variant="secondary" type="button" onClick={() => applyPreset(p.key)}>
              {p.label}
            </Button>
          ))}
        </div>
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          <Input
            label="Code *"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
            placeholder="e.g. SUPERVISOR"
            disabled={!!editing}
          />
          <Input
            label="Role name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Floor Supervisor"
          />
          <Input
            label="Description"
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What this role is for"
          />
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={roleActive} onChange={(e) => setRoleActive(e.target.checked)} />
            Active role
          </label>
        </div>
        <p className="label mb-2">Permissions by module</p>
        <PermissionMatrix
          permissions={permissions}
          enabledModules={enabledModuleCodes}
          selectedIds={form.permissionIds ?? []}
          onChange={(ids) => setForm({ ...form, permissionIds: ids })}
        />
      </Modal>
    </div>
  );
}
